<template>
  <div
    id="vuethesearch"
    ref="TheSearchAI"
    :class="
      bemBlockMod({
        open: openModal,
      })
    "
  >
    <BaseVueTyper
      v-if="showInputSpan"
      :class="bemElement('input')"
      v-bind="vueTyperData"
      @input-click="show"
    />
    <BaseTooltip
      v-if="(!showsModalOpen || showInputSpan) && tooltipData.tooltipEnabled"
      v-bind="tooltipConfig"
      :call-close-event="openModal === true"
      @tooltip-show="onToolTipShow"
      @tooltip-hide="onToolTipHide"
      @tooltip-click="onToolTipClick"
    />
    <BaseImageSearch
      v-if="imageSearchEnabled && showInputSpan"
      :class="bemElement('image-search')"
    />
    <BaseModal
      bem="the-search-ai-modal"
      :is-open="openModal"
      :modal-modifier="'fullscreen'"
      :show-backdrop="true"
      @closed="hide"
    >
      <BasePageRender
        v-if="openModal"
        :page-id="pageId"
        :query-parameters="
          searchQuery
            ? {
                searchQuery,
              }
            : {}
        "
        :search-prop="{
          defaults: {
            hidden: false,
            interacting: false,
            infiniteScrollWrapper: '.the-search-ai-modal__body',
            value: '',
          },
          searchSource,
        }"
        @on-loaded="handlerAnalytics"
      />
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../store';
import BaseModal from './BaseModal.vue';
import BasePageRender from './BasePageRender.vue';
import BaseImageSearch from './BaseImageSearch.vue';
import BaseTooltip from './BaseTooltip.vue';
import BaseVueTyper from './BaseVueTyper.vue';
import bemClassesMixin from '../mixins/bemClassesMixin';
import getFrontConfig from '../../common/getFrontConfig';
import { beginTracking } from '../../analytics/tracking/pageDesignerSections';
import EventBusConfig from '../config/eventBusConfig';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';

const analyticsEventBus = getAnalyticsEventBus();

const {
  searchAI: { alwaysShowsModalOpen, isImageSearchEnabled, searchModalId },
} = getFrontConfig();

export default {
  name: 'TheSearchAI',

  components: {
    BaseModal,
    BasePageRender,
    BaseImageSearch,
    BaseTooltip,
    BaseVueTyper,
  },

  mixins: [bemClassesMixin],

  props: {
    autocompleteSource: {
      type: String,
      default: '/suggest',
    },
    searchSource: {
      type: String,
      default: '/search',
    },
    showInputSpan: {
      type: Boolean,
      default: true,
    },
    vueTyperData: {
      type: Object,
      default: () => ({
        text: null,
        repeat: 'Infinity',
        shuffle: false,
        initialAction: 'typing',
        preTypeDelay: 250,
        typeDelay: 25,
        preEraseDelay: 2000,
        eraseDelay: 25,
        eraseStyle: 'backspace',
        eraseOnComplete: true,
        caretAnimation: 'blink',
      }),
    },
    tooltipData: {
      type: Object,
      default: () => ({
        tooltipEnabled: false,
        tooltipTitle: null,
        tooltipMessage: null,
        tooltipBtnText: null,
        tooltipTimeToShow: 3000,
        tooltipBgColor: null,
        tooltipTextColor: null,
        tooltipCloseBtnColor: null,
        tooltipAnimationEnabled: false,
        tooltipAnimationEnabledDesktop: false,
        tooltipAnimationBreakpoint: 1024,
        tooltipAnimationImage: null,
        tooltipAnimationImageDesktop: null,
      }),
    },
  },

  data() {
    return {
      currentQuery: null,
      pageId: searchModalId,
      imageSearchEnabled: isImageSearchEnabled,
      showsModalOpen: alwaysShowsModalOpen,
      tooltipConfig: {},
    };
  },

  computed: {
    ...mapGetters({
      searchQuery: getters.search.lastQuery,
    }),
    ...mapState({
      results: (state) => state.search.results,
      openModal: (state) => state.search.isModalOpen,
      isLoadingSearch: (state) => state.search.isLoading,
    }),
  },

  watch: {
    openModal(value) {
      this.calculateHeaderHeight();
      if (value) {
        this.$eventBus.$on('header-popup:hide', this.hide);
        this.$eventBus.$on('base-product-tile:current-url', this.hide);
      } else {
        this.$eventBus.$off('header-popup:hide', this.hide);
        this.$eventBus.$off('base-product-tile:current-url', this.hide);
      }
    },
  },

  created() {
    this.configureTooltip();
  },

  mounted() {
    this.calculateHeaderHeight();
  },

  methods: {
    configureTooltip() {
      this.tooltipConfig = {
        type: 'the-search-ai',
        title: this.tooltipData.tooltipTitle,
        text: this.tooltipData.tooltipMessage,
        button: this.tooltipData.tooltipBtnText,
        showCloseBtn: true,
        color: {
          background: this.tooltipData.tooltipBgColor,
          text: this.tooltipData.tooltipTextColor,
          closeBtn: this.tooltipData.tooltipCloseBtnColor,
        },
        position: 'the-search-ai',
        saveSession: false,
        cookieExpiredType: true,
        cookieAge: 7776000,
        timeToShow: Number(this.tooltipData.tooltipTimeToShow),
        animation: {
          animationEnabled:
            this.tooltipData.tooltipAnimationEnabled ||
            this.tooltipData.tooltipAnimationEnabledDesktop,
          animationBreakpoint: this.tooltipData.tooltipAnimationBreakpoint,
          animationImage: this.tooltipData.tooltipAnimationImage,
          animationImageDesktop: this.tooltipData.tooltipAnimationImageDesktop,
        },
      };
    },

    calculateHeaderHeight() {
      const header = document.querySelector('.header-group');
      document.documentElement.style.setProperty(
        '--header-total-height-with-search',
        `${header.offsetHeight}px`
      );
    },

    onToolTipShow() {
      // Send info to Analytics
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.searchTooltipLoaded,
        analyticsEventBus.EVENTS.PAYLOADS.searchTooltipLoaded.item.view
      );
    },

    onToolTipHide() {
      // Send info to Analytics
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.searchTooltipLoaded,
        analyticsEventBus.EVENTS.PAYLOADS.searchTooltipLoaded.item.cross
      );
    },

    onToolTipClick() {
      this.show();
      // Send info to Analytics
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.searchTooltipLoaded,
        analyticsEventBus.EVENTS.PAYLOADS.searchTooltipLoaded.item.cta
      );
    },

    hide() {
      this.$store.commit(types.search.setOpenModal, false);
      this.$eventBus.$emit(EventBusConfig.events.TheSearch.hidden, this);
    },

    show() {
      this.$store.commit(types.search.setOpenModal, true);
    },

    toggle() {
      if (!this.openModal) {
        this.show();
      } else {
        this.hide();
      }
    },

    handlerAnalytics(params) {
      beginTracking(params);
    },
  },
};
</script>
